import api from "./api";

export interface ErrorLog {
  id: string;
  source: "frontend" | "backend";
  type: string;
  message: string;
  stack?: string;
  context?: Record<string, any>;
  severity: "error" | "warning" | "critical";
  resolved: boolean;
  sessionId?: string;
  userId?: number;
  createdAt: string;
  resolvedAt?: string;
}

export interface ErrorLogFilters {
  source?: "frontend" | "backend";
  severity?: "error" | "warning" | "critical";
  resolved?: boolean;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ErrorStats {
  totalErrors: number;
  frontendErrors: number;
  backendErrors: number;
  criticalErrors: number;
  unresolvedErrors: number;
  errorsBySeverity: Array<{ severity: string; count: number }>;
  recentErrors: Array<{
    id: string;
    source: string;
    type: string;
    message: string;
    severity: string;
    createdAt: string;
  }>;
  topErrorTypes: Array<{ type: string; count: number }>;
}

export interface ErrorLoggingSettings {
  enabled: boolean;
  logFrontend: boolean;
  logBackend: boolean;
}

const LOGGING_SETTINGS_STORAGE_KEY = "error_logging_settings_cache";
const SETTINGS_CACHE_TTL = 5 * 60 * 1000;

interface CachedLoggingSettings {
  settings: ErrorLoggingSettings;
  cachedAt: number;
}

let cachedSettings: ErrorLoggingSettings | null = null;
let lastSettingsFetch = 0;

const persistLoggingSettings = (settings: ErrorLoggingSettings) => {
  cachedSettings = settings;
  lastSettingsFetch = Date.now();

  try {
    const payload: CachedLoggingSettings = { settings, cachedAt: lastSettingsFetch };
    localStorage.setItem(LOGGING_SETTINGS_STORAGE_KEY, JSON.stringify(payload));
  } catch (error: unknown) {
    console.error("Failed to persist logging settings:", error);
  }
};

const hydrateSettingsFromStorage = (): ErrorLoggingSettings | null => {
  if (cachedSettings) {
    return cachedSettings;
  }

  try {
    const rawSettings = localStorage.getItem(LOGGING_SETTINGS_STORAGE_KEY);
    if (!rawSettings) {
      return null;
    }

    const parsed = JSON.parse(rawSettings) as CachedLoggingSettings;
    cachedSettings = parsed.settings;
    lastSettingsFetch = parsed.cachedAt;
    return cachedSettings;
  } catch (error: unknown) {
    console.error("Failed to read cached logging settings:", error);
    return null;
  }
};

const shouldLog = (source: "frontend" | "backend", settings: ErrorLoggingSettings): boolean => {
  if (!settings.enabled) {
    return false;
  }

  return source === "frontend" ? settings.logFrontend : settings.logBackend;
};

const fetchAndCacheSettings = async (): Promise<ErrorLoggingSettings> => {
  const response = await api.get<ErrorLoggingSettings>("/api/errors/settings");
  persistLoggingSettings(response.data);
  return response.data;
};

const ensureLoggingSettings = async (): Promise<ErrorLoggingSettings | null> => {
  const storedSettings = hydrateSettingsFromStorage();
  const cacheIsFresh = Date.now() - lastSettingsFetch < SETTINGS_CACHE_TTL;

  if (storedSettings && cacheIsFresh) {
    return storedSettings;
  }

  try {
    const refreshedSettings = await fetchAndCacheSettings();
    return refreshedSettings;
  } catch (error: unknown) {
    console.error("Failed to refresh error logging settings:", error);
    return storedSettings ?? null;
  }
};

/**
 * Log an error to the backend
 */
export const logError = async (error: {
  type: string;
  message: string;
  stack?: string;
  context?: Record<string, any>;
  severity?: "error" | "warning" | "critical";
  sessionId?: string;
}) => {
  try {
    const settings = await ensureLoggingSettings();
    if (settings && !shouldLog("frontend", settings)) {
      console.info("Frontend error logging disabled via settings; skipping log.");
      return;
    }

    await api.post("/api/errors", {
      source: "frontend",
      ...error,
    });
  } catch (err: unknown) {
    // Silently fail if error logging fails
    console.error("Failed to log error:", err);
  }
};

/**
 * Get error logs with filters
 */
export const getErrorLogs = async (
  filters: ErrorLogFilters = {},
): Promise<{
  errors: ErrorLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}> => {
  const response = await api.get("/api/errors", {
    params: filters,
  });
  return response.data;
};

/**
 * Get error statistics
 */
export const getErrorStats = async (startDate?: string, endDate?: string): Promise<ErrorStats> => {
  const response = await api.get("/api/errors/stats", {
    params: { startDate, endDate },
  });
  return response.data;
};

/**
 * Resolve an error
 */
export const resolveError = async (errorId: string): Promise<ErrorLog> => {
  const response = await api.patch(`/api/errors/${errorId}/resolve`);
  return response.data;
};

/**
 * Resolve multiple errors
 */
export const resolveErrors = async (errorIds: string[]): Promise<{ resolved: number }> => {
  const response = await api.post("/api/errors/resolve-batch", {
    errorIds,
  });
  return response.data;
};

/**
 * Delete an error
 */
export const deleteError = async (errorId: string): Promise<void> => {
  await api.delete(`/api/errors/${errorId}`);
};

export const getErrorLoggingSettings = async (): Promise<ErrorLoggingSettings> => {
  return fetchAndCacheSettings();
};

export const updateErrorLoggingSettings = async (
  settings: Partial<ErrorLoggingSettings>,
): Promise<ErrorLoggingSettings> => {
  const response = await api.put<ErrorLoggingSettings>("/api/errors/settings", settings);
  persistLoggingSettings(response.data);
  return response.data;
};

/**
 * Global error handler for catching unhandled errors
 */
export const initializeErrorTracking = () => {
  // Catch unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    logError({
      type: "UnhandledPromiseRejection",
      message: event.reason?.message || String(event.reason),
      stack: event.reason?.stack,
      context: {
        url: window.location.href,
        userAgent: navigator.userAgent,
      },
      severity: "critical",
    });
  });

  // Catch global errors
  window.addEventListener("error", (event) => {
    logError({
      type: event.error?.name || "Error",
      message: event.message,
      stack: event.error?.stack,
      context: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
      severity: "error",
    });
  });

  // Override console.error to also log to backend
  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    originalConsoleError(...args);

    // Log to backend if it's an Error object
    if (args[0] instanceof Error) {
      logError({
        type: args[0].name || "ConsoleError",
        message: args[0].message,
        stack: args[0].stack,
        context: {
          url: window.location.href,
          userAgent: navigator.userAgent,
          additionalArgs: args.slice(1),
        },
        severity: "error",
      });
    }
  };
};
